import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Cuál es el protocolo de SAINT®?",
    answer:
      "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) es un protocolo de TMS de cinco días: 50 sesiones de estimulación theta burst intermitente (iTBS) durante cinco días consecutivos, con diez sesiones por día. Una resonancia magnética funcional (fMRI) personaliza el objetivo en la corteza prefrontal dorsolateral izquierda. Se administra con el sistema Magnus Neuromodulation System, autorizado por la FDA.",
  },
  {
    question: "¿Cuánto dura el protocolo de SAINT®?",
    answer:
      "Cinco días consecutivos. Cada día tiene diez sesiones de unos diez minutos cada una, con descansos de unos 50 minutos entre sesiones, por lo que un día completo de tratamiento dura varias horas. El curso completo son 50 sesiones, frente a unas 36 sesiones durante seis a nueve semanas para la TMS estándar.",
  },
  {
    question: "¿El protocolo de SAINT® está autorizado por la FDA?",
    answer:
      "SAINT® tiene autorización de la FDA (510(k) K220177, septiembre de 2022) para adultos con trastorno depresivo mayor cuya depresión no ha mejorado lo suficiente con antidepresivos previos. La autorización cubre el protocolo junto con el software de orientación del Magnus Neuromodulation System. Los resultados individuales varían.",
  },
  {
    question: "¿Puede cualquier clínica ofrecer el protocolo de SAINT®?",
    answer:
      "No. SAINT® se administra únicamente con el Magnus Neuromodulation System en clínicas verificadas por Magnus Medical. Una clínica puede copiar el calendario — diez sesiones al día durante cinco días — pero sin el software de orientación autorizado por la FDA no está ofreciendo SAINT®, y los resultados de los estudios de SAINT no se aplican a ese protocolo. Pregunte si la clínica usa el sistema Magnus.",
  },
  {
    question: "¿El 'protocolo Saint George' es lo mismo?",
    answer:
      "No. El 'protocolo Saint George' no es un protocolo reconocido ni autorizado por la FDA. SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) es el protocolo desarrollado en Stanford y administrado con el Magnus Neuromodulation System. Si una clínica menciona otro nombre, confirme que usa el sistema SAINT de Magnus.",
  },
  {
    question: "¿El protocolo de SAINT® requiere anestesia?",
    answer:
      "No. SAINT® es un tratamiento no invasivo y usted permanece despierto durante todo el procedimiento. No requiere inyecciones ni sedación, y por lo general puede conducir hacia y desde la clínica.",
  },
];

export const esSaintTmsProtocol = makeEsArticle({
  slug: "saint-tms-protocol",
  title: "Protocolo de SAINT®: qué es, cómo funciona y dónde se ofrece",
  description:
    "El protocolo de SAINT® explicado: 10 sesiones al día durante 5 días, orientación por fMRI y el sistema Magnus autorizado por la FDA. Qué implica, para quién es y dónde se ofrece.",
  h1: "¿Qué es el protocolo de SAINT®?",
  breadcrumbLabel: "Protocolo de SAINT®",
  shortAnswer:
    "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) es un protocolo de estimación cerebral de cinco días para el trastorno depresivo mayor. Administra 50 sesiones de estimulación theta burst intermitente (iTBS) durante cinco días consecutivos — diez sesiones al día, de unos diez minutos cada una, con descansos de 50 minutos entre ellas. Una resonancia magnética funcional (fMRI) identifica un objetivo personalizado en la corteza prefrontal dorsolateral izquierda, y el software de orientación forma parte del Magnus Neuromodulation System autorizado por la FDA. Los resultados individuales varían.",
  keyTakeaways: [
    "SAINT® son 50 sesiones de estimulación theta burst intermitente durante cinco días consecutivos.",
    "Cada día tiene diez sesiones de unos diez minutos, con descansos de 50 minutos entre ellas.",
    "Una fMRI identifica un objetivo personalizado en la corteza prefrontal dorsolateral izquierda.",
    "El software de orientación forma parte del Magnus Neuromodulation System autorizado por la FDA (K220177).",
    "SAINT® está autorizado por la FDA para adultos con trastorno depresivo mayor que no ha mejorado lo suficiente con antidepresivos previos.",
  ],
  sections: [
    {
      heading: "Respuesta rápida: qué es el protocolo de SAINT®",
      paragraphs: [
        "El protocolo de SAINT® — Stanford Accelerated Intelligent Neuromodulation Therapy — es una forma acelerada de estimulación magnética transcraneal (TMS) desarrollada en la Universidad de Stanford y administrada actualmente con el Magnus Neuromodulation System, autorizado por la FDA. Comprime lo que normalmente son seis a nueve semanas de TMS estándar en cinco días consecutivos.",
        "En una semana de SAINT®, usted recibe 50 sesiones de estimulación theta burst intermitente (iTBS): diez sesiones al día, cada una de unos diez minutos, con descansos de aproximadamente 50 minutos entre sesiones. Antes de comenzar la semana, una resonancia magnética funcional (fMRI) identifica un objetivo personalizado en la corteza prefrontal dorsolateral izquierda (DLPFC), y la neuronavegación mantiene la bobina sobre ese objetivo exacto en cada sesión.",
        "SAINT® está autorizado por la FDA para adultos con trastorno depresivo mayor (TDM) cuya depresión no ha mejorado lo suficiente con antidepresivos previos. Neuro Wellness TMS Centers of America (NWCOA) atiende a adultos de 22 años o más. Los resultados individuales varían.",
      ],
    },
    {
      heading: "Cómo funciona el protocolo de SAINT®, paso a paso",
      paragraphs: [
        "El protocolo tiene cuatro partes: imágenes cerebrales, orientación personalizada, la semana de tratamiento y el seguimiento. Esto es lo que implica cada una.",
      ],
      list: [
        "Imágenes cerebrales: Se realiza una resonancia magnética estructural (un mapa 3D detallado de la anatomía del cerebro) y una fMRI en reposo, que mide cómo se comunican distintas regiones del cerebro mientras usted permanece quieto. No se usan inyecciones ni contrastes.",
        "Orientación personalizada: El software de Magnus Medical analiza su fMRI para encontrar un punto preciso en la DLPFC izquierda — específicamente, el punto cuya actividad está más fuertemente anticorrelacionada con la corteza cingulada anterior subgenual (sgACC), una región implicada en la regulación del estado de ánimo.",
        "La semana de tratamiento: Durante cinco días consecutivos, recibe diez sesiones de iTBS por día. Cada sesión es de unos diez minutos de estimulación, seguida de un descanso de 50 minutos. En total son unas 50 sesiones y aproximadamente 90,000 pulsos magnéticos.",
        "Seguimiento: Después de la semana, su médico controla sus puntajes de estado de ánimo y coordina con usted, y con su médico remitente si lo tiene, los próximos pasos.",
      ],
    },
    {
      heading: "Cuánto dura el protocolo de SAINT®",
      paragraphs: [
        "El protocolo completo de SAINT® son cinco días consecutivos. Cada día de tratamiento incluye diez sesiones espaciadas unos 50 minutos, por lo que un día completo en la clínica dura varias horas. Usted permanece despierto durante todo el procedimiento y por lo general puede conducir hacia y desde la clínica.",
        "En comparación con la TMS estándar — que suele requerir 36 sesiones durante seis a nueve semanas — SAINT® comprime todo el curso en una semana. Esa es la principal diferencia práctica: muchos menos viajes a la clínica y un período mucho más corto antes de saber si el tratamiento está funcionando.",
      ],
    },
    {
      heading: "Por qué el protocolo y el sistema no pueden separarse",
      paragraphs: [
        "El protocolo de SAINT® y el Magnus Neuromodulation System no son elementos separados que se puedan mezclar. La autorización de la FDA (510(k) K220177, septiembre de 2022) cubre el protocolo junto con el software de orientación del sistema Magnus. La orientación personalizada por fMRI forma parte de lo que la FDA autorizó.",
        "Esto importa porque algunas clínicas ofrecen protocolos 'al estilo SAINT' o 'TMS acelerada guiada por fMRI' que copian el calendario — diez sesiones al día, cinco días, iTBS — pero no usan el sistema de orientación SAINT autorizado por la FDA. Pueden copiar el calendario, pero el software de orientación autorizado por la FDA y los datos de resultados de los estudios de SAINT no se transfieren a esos protocolos. Si una clínica no puede confirmar que usa el Magnus SAINT Neuromodulation System, no está ofreciendo SAINT®.",
      ],
    },
    {
      heading: "Para quién es el protocolo de SAINT® (y para quién no)",
      paragraphs: [
        "SAINT® está autorizado por la FDA para adultos con trastorno depresivo mayor cuya depresión no ha mejorado lo suficiente con antidepresivos previos. NWCOA atiende a adultos de 22 años o más.",
        "SAINT® no está autorizado para ansiedad, trastorno obsesivo-compulsivo, trastorno de estrés postraumático, trastorno bipolar, conmoción cerebral ni ninguna otra condición que no sea el TDM, y no es para menores. También está contraindicado para personas con metal magnético no removible en o cerca de la cabeza, ciertos dispositivos implantados o presión intracraneal elevada. Una evaluación confirma si SAINT® se ajusta a su historial.",
        "La idoneidad es clínica — depende de su diagnóstico, su historial de tratamiento y su evaluación médica, no de dónde vive. [[Compruebe si SAINT® es para usted|/am-i-a-candidate-screener]].",
      ],
    },
    {
      heading: "Dónde se ofrece el protocolo de SAINT®",
      paragraphs: [
        "SAINT® se administra únicamente con el Magnus Neuromodulation System en clínicas verificadas por Magnus Medical. NWCOA es un proveedor verificado de SAINT® en Coral Springs, Florida. Para encontrar proveedores verificados en otros estados, use el [[localizador de proveedores de Magnus|https://www.magnusmedical.com]] y nuestra [[página de ubicaciones de SAINT®|/es/learn/saint-tms-locations]].",
        "Como SAINT® toma solo una semana, muchos pacientes viajan a un proveedor verificado. Si viene de otro estado, [[consulte nuestra guía de viaje|/es/learn/travel-for-saint-tms]] para aeropuertos, alojamiento y cómo funciona la semana para quienes viajan.",
      ],
      links: [
        { to: "/es/saint", text: "Conozca más sobre SAINT®" },
        {
          to: "/es/learn/saint-tms-cost",
          text: "Vea el costo y el seguro de SAINT®",
        },
        { to: "/check-insurance-options", text: "Verificar mi seguro" },
      ],
    },
    {
      heading: "Protocolo de SAINT® frente a TMS estándar",
      paragraphs: [
        "La siguiente tabla compara el protocolo de SAINT® con la TMS estándar para que pueda ver las diferencias de un vistazo.",
      ],
      table: {
        headers: ["Elemento del protocolo", "SAINT®", "TMS estándar"],
        rows: [
          [
            "Patrón de pulsos",
            "Theta burst intermitente (iTBS)",
            "TMS repetitiva a 10 Hz",
          ],
          ["Sesiones por día", "10", "1"],
          ["Días", "5 días consecutivos", "6–9 semanas"],
          ["Sesiones totales", "50", "~36"],
          [
            "Orientación",
            "Por fMRI, personalizada por persona",
            "Punto anatómico estándar",
          ],
          [
            "Equipo",
            "Magnus Neuromodulation System",
            "Distintos equipos de TMS",
          ],
          [
            "Estado ante la FDA",
            "Autorizado (K220177) para TDM",
            "Autorizado para TDM",
          ],
        ],
      },
    },
    {
      heading: "Qué hacer a continuación",
      paragraphs: [
        "Entender el protocolo es la parte fácil. Saber si se ajusta a su historial es para lo que sirve una consulta. NWCOA es un proveedor verificado de SAINT® en Coral Springs, Florida, y lo guiaremos en su resonancia, su semana de tratamiento y lo que puede esperar, paso a paso.",
        "Puede [[iniciar la evaluación gratuita de idoneidad|/am-i-a-candidate-screener]], [[verificar sus beneficios del seguro|/check-insurance-options]] o [[conocer más sobre SAINT®|/es/saint]].",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132–141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177:716–726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32552917/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esSaintQuestions = makeEsArticle({
  slug: "saint-tms-questions-answered",
  title: "Preguntas sobre SAINT® TMS respondidas | Neuro Wellness",
  description:
    "Respuestas a preguntas comunes sobre SAINT® TMS: protocolo, idoneidad, seguridad, resultados y costo.",
  h1: "Preguntas sobre SAINT® TMS respondidas",
  breadcrumbLabel: "Preguntas sobre SAINT®",
  shortAnswer:
    "SAINT® es un protocolo de TMS acelerada de cinco días autorizado por la FDA (K220177) para adultos cuya depresión no ha mejorado con antidepresivos. Usa orientación por fMRI y 50 sesiones en cinco días. No requiere anestesia. Los resultados individuales varían. La idoneidad se confirma mediante una evaluación psiquiátrica.",
  keyTakeaways: [
    "SAINT® está autorizado por la FDA (K220177).",
    "50 sesiones en cinco días con orientación por fMRI.",
    "No requiere anestesia.",
    "Los resultados individuales varían.",
    "La idoneidad la determina un psiquiatra.",
  ],
  sections: [
    {
      heading: "Preguntas frecuentes",
      paragraphs: [
        "Estas son las preguntas que escuchamos con más frecuencia sobre SAINT® TMS, con respuestas breves y directas.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esSaintWeekDayByDay = makeEsArticle({
  slug: "saint-treatment-week-day-by-day",
  title: "Semana de tratamiento SAINT® día por día | Neuro Wellness",
  description:
    "Cómo es la semana de tratamiento SAINT® día por día: cinco días, diez sesiones por día, 50 en total, con orientación por fMRI.",
  h1: "Semana de tratamiento SAINT® día por día",
  breadcrumbLabel: "Semana de SAINT® día por día",
  shortAnswer:
    "SAINT® se administra durante cinco días consecutivos, con diez sesiones por día, para un total de 50 sesiones de estimulación theta burst intermitente (iTBS). Antes de iniciar el tratamiento, se realiza una resonancia magnética funcional (fMRI) para orientar la estimulación al circuito de cada persona. Está autorizado por la FDA (K220177) para adultos cuya depresión no ha mejorado con antidepresivos. No requiere anestesia. Los resultados individuales varían.",
  keyTakeaways: [
    "Cinco días consecutivos.",
    "Diez sesiones por día, 50 en total.",
    "Orientación por fMRI antes de iniciar.",
    "Autorizado por la FDA (K220177).",
    "No requiere anestesia.",
  ],
  sections: [
    {
      heading: "Antes del tratamiento",
      paragraphs: [
        "Antes de iniciar el tratamiento, se realiza una resonancia magnética funcional (fMRI) para orientar la estimulación al circuito de cada persona. El software de Magnus Medical identifica un objetivo personalizado en la corteza prefrontal dorsolateral izquierda.",
      ],
    },
    {
      heading: "Los cinco días",
      paragraphs: [
        "SAINT® se administra durante cinco días consecutivos, con diez sesiones por día, para un total de 50 sesiones de estimulación theta burst intermitente (iTBS). Cada sesión dura unos diez minutos, con descansos de aproximadamente 50 minutos entre sesiones. No requiere anestesia. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esStanfordAcceleratedIntelligentNeuromodulationTherapy =
  makeEsArticle({
    slug: "stanford-accelerated-intelligent-neuromodulation-therapy",
    title:
      "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®) | Neuro Wellness",
    description:
      "SAINT® significa Stanford Accelerated Intelligent Neuromodulation Therapy. Protocolo de TMS acelerada de cinco días autorizado por la FDA (K220177).",
    h1: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®)",
    breadcrumbLabel: "SAINT® (Stanford)",
    shortAnswer:
      "SAINT® significa Stanford Accelerated Intelligent Neuromodulation Therapy. Es un protocolo de TMS acelerada que administra 50 sesiones en cinco días con orientación por fMRI. Está autorizado por la FDA (K220177) para adultos cuya depresión no ha mejorado con antidepresivos. En un ensayo controlado con simulación, el 79 % del grupo activo alcanzó la remisión. Los resultados individuales varían.",
    keyTakeaways: [
      "SAINT® = Stanford Accelerated Intelligent Neuromodulation Therapy.",
      "50 sesiones en cinco días con orientación por fMRI.",
      "Autorizado por la FDA (K220177) para adultos.",
      "El 79 % del grupo activo alcanzó la remisión en el ensayo.",
      "Los resultados individuales varían.",
    ],
    sections: [
      {
        heading: "Qué significa SAINT®",
        paragraphs: [
          "SAINT® significa Stanford Accelerated Intelligent Neuromodulation Therapy. Es un protocolo de TMS acelerada desarrollado en la Universidad de Stanford que administra 50 sesiones en cinco días con orientación por fMRI.",
        ],
      },
      {
        heading: "Autorización de la FDA",
        paragraphs: [
          "SAINT® está autorizado por la FDA (K220177) para adultos con trastorno depresivo mayor que no han mejorado con antidepresivos. En un ensayo controlado con simulación, el 79 % del grupo activo alcanzó la remisión. Los resultados individuales varían. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
        ],
      },
    ],
    faqs,
    sources: [],
    relatedLinks: esSaintRelated,
    reviewBlock,
  } satisfies EsArticleInput);

export const esTravelForSaint = makeEsArticle({
  slug: "travel-for-saint-tms",
  title: "Viajar para recibir SAINT® TMS | Neuro Wellness",
  description:
    "Viajar para recibir SAINT® TMS: por qué el protocolo de cinco días hace que viajar sea práctico y qué esperar en Coral Springs, FL.",
  h1: "Viajar para recibir SAINT® TMS",
  breadcrumbLabel: "Viajar para SAINT®",
  shortAnswer:
    "El protocolo de cinco días de SAINT® hace que viajar sea práctico: el tratamiento se completa en una sola semana. Neuro Wellness TMS Centers of America ofrece SAINT® en Coral Springs, FL. SAINT® está autorizado por la FDA (K220177) para adultos cuya depresión no ha mejorado con antidepresivos. [[Vea información sobre viajar para SAINT®|/es/saint-tms-travel]] o llame al 954-827-9793.",
  keyTakeaways: [
    "El protocolo de cinco días hace que viajar sea práctico.",
    "El tratamiento se completa en una semana.",
    "SAINT® se ofrece en Coral Springs, FL.",
    "Autorizado por la FDA (K220177).",
  ],
  sections: [
    {
      heading: "Por qué viajar para SAINT®",
      paragraphs: [
        "SAINT® se completa en cinco días, por lo que viajar para recibirlo es práctico. Neuro Wellness TMS Centers of America ofrece SAINT® en Coral Springs, FL. [[Vea información sobre viajar para SAINT®|/es/saint-tms-travel]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esTherapyNotHelping = makeEsArticle({
  slug: "what-if-therapy-isnt-helping-depression",
  title: "¿Qué pasa si la terapia no ayuda a la depresión? | Neuro Wellness",
  description:
    "Si la psicoterapia por sí sola no alivia la depresión, existen opciones como antidepresivos, TMS, SAINT® y SPRAVATO®.",
  h1: "¿Qué pasa si la terapia no ayuda a la depresión?",
  breadcrumbLabel: "La terapia no ayuda",
  shortAnswer:
    "La psicoterapia es un tratamiento de primera línea, pero por sí sola no siempre es suficiente. Si la terapia no ha producido alivio suficiente, las opciones incluyen antidepresivos, TMS, SAINT® TMS acelerada y SPRAVATO®. La depresión resistente al tratamiento se define como la falta de respuesta a al menos dos antidepresivos en dosis y duración adecuadas. Una evaluación psiquiátrica puede identificar los siguientes pasos.",
  keyTakeaways: [
    "La terapia por sí sola no siempre es suficiente.",
    "Las opciones incluyen antidepresivos, TMS, SAINT® y SPRAVATO®.",
    "La depresión resistente = falta de respuesta a ≥2 antidepresivos.",
    "Una evaluación psiquiátrica identifica los siguientes pasos.",
  ],
  sections: [
    {
      heading: "Cuando la terapia no es suficiente",
      paragraphs: [
        "La psicoterapia es eficaz para muchos, pero la depresión a veces requiere más que terapia. Combinar terapia con medicación o tratamientos sin medicación puede ayudar.",
      ],
    },
    {
      heading: "Opciones más allá de la terapia",
      paragraphs: [
        "La TMS está autorizada por la FDA para la depresión que no ha respondido a medicación. SAINT® es un protocolo de TMS acelerada de cinco días autorizado por la FDA (K220177). SPRAVATO® está aprobado por la FDA para la depresión resistente al tratamiento. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esTmsAfterAntidepressantsFail = makeEsArticle({
  slug: "tms-after-antidepressants-fail",
  title: "TMS después de que los antidepresivos fallan | Neuro Wellness",
  description:
    "TMS después de que los antidepresivos no han funcionado: cómo la TMS y SAINT® pueden ayudar cuando la medicación no es suficiente.",
  h1: "TMS después de que los antidepresivos fallan",
  breadcrumbLabel: "TMS después de antidepresivos",
  shortAnswer:
    "Cuando los antidepresivos no han aliviado la depresión, la TMS y SAINT® son opciones. La TMS está autorizada por la FDA para la depresión que no ha respondido a medicación. SAINT® es un protocolo de TMS acelerada de cinco días autorizado por la FDA (K220177). Los resultados individuales varían; ningún tratamiento ofrece garantía. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
  keyTakeaways: [
    "La TMS está autorizada por la FDA para la depresión resistente.",
    "SAINT® comprime el tratamiento en cinco días.",
    "Los resultados individuales varían.",
    "Ningún tratamiento ofrece garantía.",
  ],
  sections: [
    {
      heading: "Cuando la medicación no es suficiente",
      paragraphs: [
        "La depresión resistente al tratamiento se define como la falta de respuesta a al menos dos antidepresivos en dosis y duración adecuadas. La TMS y SAINT® son opciones cuando la medicación no ha funcionado.",
      ],
    },
    {
      heading: "TMS y SAINT®",
      paragraphs: [
        "La TMS está autorizada por la FDA para la depresión que no ha respondido a medicación. SAINT® es un protocolo de TMS acelerada de cinco días autorizado por la FDA (K220177). [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esTiredOfFeelingDepressed = makeEsArticle({
  slug: "tired-of-feeling-depressed",
  title: "Cansado de sentirse deprimido | Neuro Wellness",
  description:
    "Si está cansado de sentirse deprimido y la medicación no ha funcionado, existen opciones como TMS, SAINT® y SPRAVATO®.",
  h1: "Cansado de sentirse deprimido",
  breadcrumbLabel: "Cansado de sentirse deprimido",
  shortAnswer:
    "Si está cansado de sentirse deprimido y los antidepresivos no han ayudado, existen opciones. La TMS está autorizada por la FDA para la depresión que no ha respondido a medicación. SAINT® es un protocolo de TMS acelerada de cinco días autorizado por la FDA (K220177). Los resultados individuales varían. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
  keyTakeaways: [
    "Existen opciones cuando la medicación no ha funcionado.",
    "La TMS está autorizada por la FDA para la depresión resistente.",
    "SAINT® comprime el tratamiento en cinco días.",
    "Los resultados individuales varían.",
  ],
  sections: [
    {
      heading: "Cuando la medicación no es suficiente",
      paragraphs: [
        "Si está cansado de sentirse deprimido y los antidepresivos no han ayudado, la TMS y SAINT® son opciones. La depresión resistente al tratamiento se define como la falta de respuesta a al menos dos antidepresivos.",
      ],
    },
    {
      heading: "Los siguientes pasos",
      paragraphs: [
        "Una evaluación psiquiátrica puede identificar si SAINT® es apropiado para usted. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esTiredOfFeelingDepressedOptionsLeft = makeEsArticle({
  slug: "tired-of-feeling-depressed-options-left",
  title: "Opciones cuando está cansado de sentirse deprimido | Neuro Wellness",
  description:
    "Opciones cuando está cansado de sentirse deprimido: TMS, SAINT®, SPRAVATO® y más, en Coral Springs, FL.",
  h1: "Opciones cuando está cansado de sentirse deprimido",
  breadcrumbLabel: "Opciones para la depresión",
  shortAnswer:
    "Si está cansado de sentirse deprimido, existen opciones más allá de los antidepresivos. La TMS está autorizada por la FDA para la depresión que no ha respondido a medicación. SAINT® es un protocolo de TMS acelerada de cinco días autorizado por la FDA (K220177). Los resultados individuales varían. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
  keyTakeaways: [
    "Existen opciones más allá de los antidepresivos.",
    "La TMS está autorizada por la FDA para la depresión resistente.",
    "SAINT® comprime el tratamiento en cinco días.",
    "Los resultados individuales varían.",
  ],
  sections: [
    {
      heading: "Opciones disponibles",
      paragraphs: [
        "Además de los antidepresivos, existen la TMS, SAINT® y SPRAVATO®. La TMS está autorizada por la FDA para la depresión que no ha respondido a medicación.",
      ],
    },
    {
      heading: "Los siguientes pasos",
      paragraphs: [
        "Una evaluación psiquiátrica puede identificar los siguientes pasos. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);

export const esWhyDoesMyDepressionKeepComingBack = makeEsArticle({
  slug: "why-does-my-depression-keep-coming-back",
  title: "¿Por qué mi depresión sigue volviendo? | Neuro Wellness",
  description:
    "Por qué la depresión sigue volviendo y qué hacer al respecto: opciones de tratamiento en Coral Springs, FL.",
  h1: "¿Por qué mi depresión sigue volviendo?",
  breadcrumbLabel: "La depresión sigue volviendo",
  shortAnswer:
    "La depresión puede volver por varias razones: tratamiento insuficiente, dosis subóptimas o falta de mantenimiento. Cuando la depresión sigue volviendo a pesar de los antidepresivos, la TMS y SAINT® son opciones. SAINT® es un protocolo de TMS acelerada de cinco días autorizado por la FDA (K220177). Los resultados individuales varían. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
  keyTakeaways: [
    "La depresión puede volver por tratamiento insuficiente o dosis subóptimas.",
    "La TMS está autorizada por la FDA para la depresión resistente.",
    "SAINT® comprime el tratamiento en cinco días.",
    "Los resultados individuales varían.",
  ],
  sections: [
    {
      heading: "Por qué la depresión vuelve",
      paragraphs: [
        "La depresión puede volver porque el tratamiento no fue suficiente, la dosis no fue la adecuada o no hubo mantenimiento. Cuando los antidepresivos no han sido suficientes, la TMS y SAINT® son opciones.",
      ],
    },
    {
      heading: "Los siguientes pasos",
      paragraphs: [
        "Una evaluación psiquiátrica puede identificar por qué la depresión sigue volviendo y qué hacer al respecto. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
